import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { translate } from '@smart-home/i18n';
import type { LanguageCode } from '@smart-home/shared';
import { updateUserPreferences } from '../api/user';
import { useAuthStore } from '../store/authStore';

const STORAGE_KEY = 'smartHome.language';

type I18nContextValue = {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const updatePreferences = useAuthStore((state) => state.updatePreferences);
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'en' ? 'en' : 'vi';
  });

  useEffect(() => {
    const backendLanguage = user?.preferences?.language;
    if (backendLanguage && backendLanguage !== language) {
      setLanguageState(backendLanguage);
    }
  }, [user?.preferences?.language]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (nextLanguage: LanguageCode) => {
    setLanguageState(nextLanguage);
    localStorage.setItem(STORAGE_KEY, nextLanguage);

    if (user) {
      const nextPreferences = { ...(user.preferences || {}), language: nextLanguage };
      updatePreferences(nextPreferences);
      updateUserPreferences({ language: nextLanguage }).catch(() => {
        // keep optimistic local update for offline preview/memory mode
      });
    }
  };

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    t: (key) => translate(language, key)
  }), [language, user?.id]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
}
